import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import Link from 'next/link'
import { cn } from './cn'

/**
 * Bouton.
 *
 * Les variantes reprennent celles du design system (`primary`, `accent`, `ghost`,
 * `text`, `onInk`) et leurs bascules de survol exactes. L'échelle, en revanche,
 * est celle de l'app : le DS est calibré pour le site marketing (15px, 14/22 de
 * marge intérieure), ce qui est trop massif dans un tableau de bord dense. Le
 * handoff documente sa propre échelle, c'est elle qui s'applique ici.
 *
 * Le survol ne joue jamais sur l'opacité — toujours sur la couleur.
 */

type Variant =
  | 'primary'
  | 'accent'
  | 'ghost'
  | 'text'
  | 'onInk'
  // Oasis 2027 — boutons « stickers » (voir `STICKERS` ci-dessous).
  | 'sticker'
  | 'action'
  | 'trait'
  | 'lavande'
  | 'rose'
type Size = 'sm' | 'md'

const VARIANTS: Record<Variant, string> = {
  // Action principale d'un écran clair.
  primary: 'bg-ink text-cream border-transparent hover:bg-lavender hover:text-ink',
  // Action mise en avant : la lavande est l'accent, on la garde rare.
  accent: 'bg-lavender text-ink border-transparent hover:bg-lavender-deep hover:text-cream',
  // Action secondaire.
  ghost: 'bg-transparent text-ink border-ink hover:bg-ink hover:text-cream',
  // Action tertiaire, alignée au texte : ni fond, ni bordure, ni rayon.
  text: 'bg-transparent text-ink border-transparent hover:text-lavender-deep px-1 rounded-none',
  // Sur bandeau encre : la lavande devient la surface, l'encre le texte.
  onInk: 'bg-lavender text-ink border-transparent hover:bg-cream',
  /* ---- Oasis 2027 : ne s'emploient que sous `[data-da="2027"]` ----
     Découpe crème de 5px, ombre douce, inclinaison de 2° qui bascule au survol.
     `action` (jaune cerclé de prune) est réservé à l'action principale d'un
     écran : un seul par écran. */
  sticker:
    'bg-ink text-surface shadow-[0_0_0_5px_var(--creme),0_8px_14px_rgba(43,23,72,0.22)] hover:shadow-[0_0_0_5px_var(--creme),0_14px_20px_rgba(43,23,72,0.28)]',
  action:
    'bg-jaune text-ink shadow-[inset_0_0_0_2px_var(--ink),0_0_0_5px_var(--creme),0_8px_14px_rgba(43,23,72,0.22)] hover:shadow-[inset_0_0_0_2px_var(--ink),0_0_0_5px_var(--creme),0_14px_20px_rgba(43,23,72,0.28)]',
  trait:
    'bg-surface text-ink shadow-[inset_0_0_0_2px_var(--ink),0_0_0_5px_var(--creme),0_8px_14px_rgba(43,23,72,0.18)] hover:shadow-[inset_0_0_0_2px_var(--ink),0_0_0_5px_var(--creme),0_14px_20px_rgba(43,23,72,0.24)]',
  lavande:
    'bg-lavender text-ink shadow-[0_0_0_5px_var(--creme),0_8px_14px_rgba(43,23,72,0.22)] hover:shadow-[0_0_0_5px_var(--creme),0_14px_20px_rgba(43,23,72,0.28)]',
  rose: 'bg-rose text-ink shadow-[0_0_0_5px_var(--creme),0_8px_14px_rgba(43,23,72,0.22)] hover:shadow-[0_0_0_5px_var(--creme),0_14px_20px_rgba(43,23,72,0.28)]',
}

/** Les variantes 2027 ont leur propre ossature : pas de bordure, corps 15px en 800, inclinaison. */
const STICKERS: ReadonlySet<Variant> = new Set(['sticker', 'action', 'trait', 'lavande', 'rose'])

/* ⚠️ La taille ne s'applique pas aux stickers : leur rembourrage est dans
   `BASE_STICKER`. Les cumuler laisserait deux `padding` en concurrence, réglés
   par l'ordre du CSS — `cn` ne fusionne pas les classes. */
const BASE_STICKER = cn(
  'inline-flex min-h-11 items-center justify-center gap-2.5 rounded-pill px-[22px] py-[13px]',
  'text-[15px] font-extrabold tracking-[-0.02em] whitespace-nowrap cursor-pointer',
  '-rotate-2 transition-[transform,box-shadow] duration-(--dur-base) ease-(--ease-umiros)',
  'hover:-translate-y-[3px] hover:rotate-3 active:translate-y-px active:rotate-0',
  'disabled:cursor-not-allowed disabled:opacity-100 disabled:bg-cream-warm disabled:text-ink-mute',
)

const SIZES: Record<Size, string> = {
  sm: 'text-[13px] px-3.5 py-2 gap-1.5',
  md: 'text-[14px] px-[18px] py-[11px] gap-2',
}

const BASE = cn(
  'inline-flex items-center justify-center rounded-pill border-[1.5px]',
  'font-semibold tracking-[-0.01em] whitespace-nowrap cursor-pointer',
  'disabled:cursor-not-allowed disabled:bg-cream-warm disabled:text-ink-mute',
  'disabled:border-transparent disabled:hover:bg-cream-warm disabled:hover:text-ink-mute',
)

function classesBouton(variant: Variant, size: Size, className?: string) {
  return STICKERS.has(variant)
    ? cn(BASE_STICKER, VARIANTS[variant], className)
    : cn(BASE, VARIANTS[variant], SIZES[size], className)
}

type CommonProps = {
  variant?: Variant
  size?: Size
  /** Placé après le libellé, comme dans le design system. */
  icon?: ReactNode
  children: ReactNode
  className?: string
}

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  children,
  className,
  ...rest
}: CommonProps & ComponentPropsWithoutRef<'button'>) {
  return (
    <button className={classesBouton(variant, size, className)} {...rest}>
      {children}
      {icon}
    </button>
  )
}

/**
 * Même apparence, mais navigue. À utiliser dès que l'action mène à une autre
 * route : un `<button>` avec un `router.push` casse le clic-milieu, l'ouverture
 * en nouvel onglet et la copie du lien.
 */
export function ButtonLink({
  variant = 'primary',
  size = 'md',
  icon,
  children,
  className,
  ...rest
}: CommonProps & ComponentPropsWithoutRef<typeof Link>) {
  return (
    <Link className={classesBouton(variant, size, className)} {...rest}>
      {children}
      {icon}
    </Link>
  )
}
